import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SecurityIcon from '@mui/icons-material/Security'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import { Oval } from 'react-loader-spinner'
import Swal from 'sweetalert2'

import {
  get2FAStatus,
  setup2FA,
  verify2FA,
  disable2FA,
} from 'service/TwoFactorService'

const TwoFactorSetupPage = () => {
  const { t } = useTranslation('common')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [setupData, setSetupData] = useState(null)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')

  const loadStatus = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await get2FAStatus()
      setEnabled(!!(data?.enabled || data?.is_enabled))
    } catch (e) {
      setError(e?.response?.data?.detail || t('auth.twofa_status_failed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetup = async () => {
    setBusy(true)
    setError('')
    try {
      const { data } = await setup2FA()
      setSetupData(data)
    } catch (e) {
      setError(e?.response?.data?.detail || t('auth.twofa_settings_failed'))
    } finally {
      setBusy(false)
    }
  }

  const handleVerify = async () => {
    if (!code || code.length < 6) {
      setError(t('auth.enter_6_code'))
      return
    }
    setBusy(true)
    setError('')
    try {
      await verify2FA(code)
      Swal.fire({ title: t('auth.twofa_enabled'), icon: 'success', timer: 2000, showConfirmButton: false })
      setSetupData(null)
      setCode('')
      await loadStatus()
    } catch (e) {
      setError(e?.response?.data?.detail || t('auth.wrong_code_retry'))
    } finally {
      setBusy(false)
    }
  }

  const handleDisable = async () => {
    if (!password) {
      setError(t('auth.enter_password_confirm'))
      return
    }
    const ok = await Swal.fire({
      title: t('auth.twofa_disable_q'),
      text: t('auth.twofa_less_secure'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('auth.twofa_disable_btn'),
      cancelButtonText: t('common.cancel'),
    })
    if (!ok.isConfirmed) return
    setBusy(true)
    setError('')
    try {
      await disable2FA(password)
      Swal.fire({ title: t('auth.twofa_disabled'), icon: 'success', timer: 2000, showConfirmButton: false })
      setPassword('')
      await loadStatus()
    } catch (e) {
      setError(e?.response?.data?.detail || t('auth.twofa_disable_failed'))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Oval height={48} width={48} color="#FF7A00" secondaryColor="#ffe0c2" />
      </Box>
    )
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          maxWidth: 480,
          mx: 'auto',
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <SecurityIcon sx={{ color: '#FF7A00', fontSize: 36 }} />
            <Typography variant="h5" fontWeight={700}>
              {t('auth.twoFactor')}
            </Typography>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {enabled && !setupData && (
            <>
              <Alert severity="success" icon={<VerifiedUserIcon />}>
                {t('auth.twofa_enabled_desc')}
              </Alert>
              <TextField
                type="password"
                label={t('auth.current_password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
              />
              <Button
                color="error"
                variant="outlined"
                startIcon={<LockOpenIcon />}
                onClick={handleDisable}
                disabled={busy}
              >
                {t('auth.twofa_disable_btn')}
              </Button>
            </>
          )}

          {!enabled && !setupData && (
            <>
              <Typography color="text.secondary">
                {t('auth.twofa_not_set')}
              </Typography>
              <Button
                variant="contained"
                onClick={handleSetup}
                disabled={busy}
                sx={{ bgcolor: '#FF7A00', '&:hover': { bgcolor: '#e96d00' } }}
              >
                {t('auth.configure')}
              </Button>
            </>
          )}

          {setupData && (
            <Stack spacing={2} alignItems="center">
              <Typography>
                {t('auth.scan_qr_hint')}
              </Typography>
              {setupData.qr_base64 && (
                <Box
                  component="img"
                  src={
                    setupData.qr_base64.startsWith('data:')
                      ? setupData.qr_base64
                      : `data:image/png;base64,${setupData.qr_base64}`
                  }
                  alt="2FA QR"
                  sx={{ width: 220, height: 220, border: '1px solid #eee', p: 1, borderRadius: 1 }}
                />
              )}
              {setupData.secret && (
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {t('auth.secret_key_label')}: {setupData.secret}
                </Typography>
              )}
              <TextField
                label={t('auth.code_from_app')}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
                size="medium"
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  style: {
                    textAlign: 'center',
                    fontSize: 22,
                    letterSpacing: '0.4em',
                    fontWeight: 700,
                  },
                }}
              />
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <Button fullWidth variant="outlined" onClick={() => setSetupData(null)} disabled={busy}>
                  {t('common.cancel')}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleVerify}
                  disabled={busy || code.length !== 6}
                  sx={{ bgcolor: '#FF7A00', '&:hover': { bgcolor: '#e96d00' } }}
                >
                  {t('common.confirm')}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Container>
  )
}

export default TwoFactorSetupPage
